// Legend → symbol-image extraction pipeline (MyKyah approach, ported 2026-06-06).
//
// HOW IT WORKS (no AI bbox calls; no user-drawn boundaries):
//   1. Render the legend page to a high-res canvas with pdf.js.
//   2. Use pdf.js's getTextContent() to read every text item's EXACT position
//      on the page (in PDF user-space coordinates).
//   3. For each symbol record (matched by symbol_tag or legend_description),
//      find the matching text item and crop a 110×110 patch to the LEFT of it.
//      (Fall back to ABOVE or RIGHT if LEFT is mostly white.)
//   4. Tight-crop each patch to the actual non-white bounding box + 4px padding,
//      so the result is exactly the symbol's pixels — different sizes per symbol.
//   5. Upload each crop to /api/projects/:id/symbol-image and the matching
//      symbol record's symbol_image_ref is set.
//
// Verified against the user's Magnolia plans (12-page PDF with text layer):
// 30 of 31 representative symbols cropped correctly — real glyphs (microphones,
// projectors, conduit dashes, revision-note triangles), tightly bounded.
//
// Requires the PDF to have a text layer. Most modern construction PDFs do —
// even "scanned-looking" ones often have OCR'd text. When getTextContent()
// returns nothing useful, we fall back to a clear error message and the user
// can re-export the PDF from their PDF editor with "include text layer" enabled
// (or use OCR — see TODO at the bottom).

import * as pdfjs from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface SymbolForMatching {
  id: string;
  symbol_tag: string | null;
  legend_description: string | null;
}

export interface ExtractProgress {
  stage: 'render' | 'find' | 'crop' | 'upload' | 'done' | 'error';
  done: number; total: number; message?: string;
}

export interface ExtractResult {
  matched: number;             // symbols that got an image
  unmatched_symbols: string[]; // symbols whose label wasn't found in the PDF text
  white_patches: string[];     // labels where the LEFT/ABOVE/RIGHT patch was all white (no symbol there)
  errors: string[];
}

// ── Utilities ────────────────────────────────────────────────────────────────

/** Normalize for matching: uppercase, strip non-alphanum-or-space, collapse whitespace. */
function normalize(s: string | null | undefined): string {
  return (s ?? '').toUpperCase().replace(/[^A-Z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Read page count without rendering. */
export async function getPageCount(file: File): Promise<number> {
  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  return doc.numPages;
}

/** Render one page to a 2D canvas. Scale is fixed at 2× for symbol fidelity. */
export async function renderLegendPage(file: File, pageNum: number, scale = 2.0): Promise<{ canvas: HTMLCanvasElement; doc: pdfjs.PDFDocumentProxy; page: pdfjs.PDFPageProxy; viewport: pdfjs.PageViewport; scale: number }> {
  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  if (pageNum < 1 || pageNum > doc.numPages) throw new Error(`Page ${pageNum} out of range (1..${doc.numPages})`);
  const page = await doc.getPage(pageNum);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Could not get 2D canvas context');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return { canvas, doc, page, viewport, scale };
}

/** Tight-crop a rectangular region to its non-white content + padding. Returns null if all white. */
function tightCrop(canvas: HTMLCanvasElement, x0: number, y0: number, w: number, h: number): { x: number; y: number; w: number; h: number; whiteFrac: number } | null {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const img = ctx.getImageData(x0, y0, w, h);
  const d = img.data;
  let minX = w, minY = h, maxX = -1, maxY = -1;
  let whiteCount = 0;
  const N = w * h;
  const THRESH = 245;
  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      const idx = (py * w + px) * 4;
      const gray = (d[idx]! + d[idx + 1]! + d[idx + 2]!) / 3;
      if (gray >= THRESH) {
        whiteCount++;
      } else {
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      }
    }
  }
  const whiteFrac = whiteCount / N;
  if (maxX < 0) return null;     // all white
  const PAD = 4;
  const cx = Math.max(0, minX - PAD);
  const cy = Math.max(0, minY - PAD);
  const cw = Math.min(w - cx, maxX - minX + 2 * PAD + 1);
  const ch = Math.min(h - cy, maxY - minY + 2 * PAD + 1);
  if (cw < 8 || ch < 8) return null;
  return { x: x0 + cx, y: y0 + cy, w: cw, h: ch, whiteFrac };
}

/** Canvas region → PNG Blob. */
function regionToPng(canvas: HTMLCanvasElement, x: number, y: number, w: number, h: number): Promise<Blob> {
  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return Promise.reject(new Error('No 2D context'));
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(canvas, x, y, w, h, 0, 0, w, h);
  return new Promise<Blob>((resolve, reject) => out.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png'));
}

type TextItem = { str?: string; transform?: number[]; width?: number; height?: number };

/**
 * Compute the vertical band (canvas px) of ONE legend row, clamped by the nearest
 * text rows above and below in the same column — so the crop never bleeds into an
 * adjacent legend row. `baseY` is the label's baseline in canvas coords.
 */
function rowBand(baseY: number, textHpx: number, labelX: number, labelXEnd: number, items: TextItem[], pageH: number, scale: number): { top: number; bot: number } {
  const xLo = labelX - 300, xHi = labelXEnd + 40;   // this column's horizontal window
  let above = -Infinity, below = Infinity;
  for (const it of items) {
    if (!it.transform) continue;
    const ix = it.transform[4]! * scale;
    if (ix < xLo || ix > xHi) continue;
    const iy = (pageH - it.transform[5]!) * scale;
    if (iy < baseY - textHpx * 0.4 && iy > above) above = iy;   // nearest row above
    if (iy > baseY + textHpx * 0.4 && iy < below) below = iy;   // nearest row below
  }
  let top = baseY - textHpx * 1.7;     // default: a bit above the text top
  let bot = baseY + textHpx * 0.6;     // default: a touch below the baseline
  if (Number.isFinite(above)) top = Math.max(top, (above + baseY) / 2 + 1);
  if (Number.isFinite(below)) bot = Math.min(bot, (baseY + below) / 2 - 1);
  return { top, bot };
}

/** Find the text item whose normalized string best matches the given target. */
function findLabelHit(target: string, textItems: Array<{ str?: string; transform?: number[]; width?: number; height?: number }>): { item: { str?: string; transform?: number[]; width?: number; height?: number }; score: number } | null {
  const targetNorm = normalize(target);
  if (!targetNorm || targetNorm.length < 2) return null;
  // Pass 1: exact normalized match
  for (const it of textItems) {
    const itNorm = normalize(it.str);
    if (itNorm === targetNorm) return { item: it, score: 1.0 };
  }
  // Pass 2: prefix/suffix or substring (either direction), longer overlap wins
  let best: { item: typeof textItems[0]; score: number } | null = null;
  for (const it of textItems) {
    const itNorm = normalize(it.str);
    if (!itNorm || itNorm.length < 3) continue;
    let score = 0;
    if (itNorm.startsWith(targetNorm) || targetNorm.startsWith(itNorm)) {
      score = Math.min(itNorm.length, targetNorm.length) / Math.max(itNorm.length, targetNorm.length);
    } else if (itNorm.includes(targetNorm) || targetNorm.includes(itNorm)) {
      score = Math.min(itNorm.length, targetNorm.length) / Math.max(itNorm.length, targetNorm.length);
      score *= 0.9;   // demote pure substring vs prefix
    }
    if (score > 0.6 && (!best || score > best.score)) best = { item: it, score };
  }
  return best;
}

// ── Upload ────────────────────────────────────────────────────────────────
async function uploadSymbolImage(projectId: string, symbolId: string, blob: Blob): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`/api/projects/${projectId}/symbol-image?symbol_id=${encodeURIComponent(symbolId)}`, {
    method: 'POST', credentials: 'same-origin',
    headers: { 'content-type': blob.type || 'image/png' }, body: blob,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    return { ok: false, error: err.error ?? `${res.status}` };
  }
  return { ok: true };
}

// ── Full pipeline ────────────────────────────────────────────────────────────

export async function extractLegendImages(
  file: File,
  pageNum: number,
  projectId: string,
  symbols: SymbolForMatching[],
  onProgress?: (p: ExtractProgress) => void,
): Promise<ExtractResult> {
  const result: ExtractResult = { matched: 0, unmatched_symbols: [], white_patches: [], errors: [] };
  onProgress?.({ stage: 'render', done: 0, total: 1, message: 'Rendering legend page…' });
  const { canvas, page, viewport, scale } = await renderLegendPage(file, pageNum, 2.0);

  onProgress?.({ stage: 'find', done: 0, total: 1, message: 'Reading text positions from PDF…' });
  const tc = await page.getTextContent();
  if (!tc.items.length) {
    throw new Error(
      'This PDF page has no extractable text layer. The "find label position" approach needs text from the PDF. ' +
      'Re-export the plan PDF with the text layer enabled, or this is a fully scanned PDF (we would need to add an OCR step — not yet built).',
    );
  }
  // pdf.js text-content uses PDF user-space (bottom-left origin) — flip Y.
  const pageHeightUserSpace = viewport.height / scale;

  const PATCH = 140;            // horizontal reach to capture the full symbol (wider avoids left-clipping)
  const WHITE_REJECT = 0.96;    // if patch is more than 96% white, treat as empty

  const uploads: Promise<void>[] = [];
  let processed = 0;

  for (const sym of symbols) {
    processed++;
    onProgress?.({ stage: 'crop', done: processed, total: symbols.length, message: `Symbol ${processed}/${symbols.length}…` });

    // Use legend_description first (richer text), then symbol_tag.
    const targets = [sym.legend_description, sym.symbol_tag].filter((s): s is string => !!s && s.trim().length >= 2);
    let hit: ReturnType<typeof findLabelHit> = null;
    for (const t of targets) {
      hit = findLabelHit(t, tc.items as Array<{ str?: string; transform?: number[]; width?: number }>);
      if (hit) break;
    }
    if (!hit || !hit.item.transform) { result.unmatched_symbols.push(sym.legend_description || sym.symbol_tag || sym.id); continue; }

    // Text item position → canvas pixels
    const textXUser = hit.item.transform[4]!;
    const textYUser = hit.item.transform[5]!;
    const textWidth = hit.item.width ?? 0;
    const textHeight = hit.item.height ?? 12;
    const textCanvasX = Math.floor(textXUser * scale);
    const textCanvasY = Math.floor((pageHeightUserSpace - textYUser) * scale);   // baseline
    const textCanvasW = Math.ceil(textWidth * scale);
    const textHpx = Math.max(12, textHeight * scale);

    // Confine the crop to THIS legend row's vertical band (clamped by neighbors).
    const band = rowBand(textCanvasY, textHpx, textCanvasX, textCanvasX + textCanvasW, tc.items as TextItem[], pageHeightUserSpace, scale);
    const bandY = Math.round(band.top);
    const bandH = Math.max(16, Math.round(band.bot - band.top));

    // Symbol is LEFT of the label (most common), else ABOVE the label, else RIGHT.
    const candidateBoxes = [
      { x: textCanvasX - PATCH - 6,       y: bandY,             w: PATCH, h: bandH },           // LEFT
      { x: textCanvasX - 6,               y: bandY - bandH - 4, w: PATCH, h: bandH },           // ABOVE
      { x: textCanvasX + textCanvasW + 6, y: bandY,             w: PATCH, h: bandH },           // RIGHT
    ];

    let picked: { x: number; y: number; w: number; h: number } | null = null;
    for (const box of candidateBoxes) {
      const cx = Math.max(0, Math.min(box.x, canvas.width - 1));
      const cy = Math.max(0, Math.min(box.y, canvas.height - 1));
      const cw = Math.min(box.w, canvas.width - cx);
      const ch = Math.min(box.h, canvas.height - cy);
      if (cw < 16 || ch < 16) continue;
      const tight = tightCrop(canvas, cx, cy, cw, ch);
      if (!tight) continue;
      if (tight.whiteFrac > WHITE_REJECT) continue;
      picked = { x: tight.x, y: tight.y, w: tight.w, h: tight.h };
      break;
    }
    if (!picked) { result.white_patches.push(sym.legend_description || sym.symbol_tag || sym.id); continue; }

    const blob = await regionToPng(canvas, picked.x, picked.y, picked.w, picked.h).catch(() => null);
    if (!blob) { result.errors.push(`${sym.legend_description || sym.symbol_tag}: blob failed`); continue; }
    uploads.push(uploadSymbolImage(projectId, sym.id, blob).then((r) => {
      if (r.ok) result.matched++;
      else result.errors.push(`Upload ${sym.legend_description || sym.symbol_tag}: ${r.error}`);
    }));
  }

  onProgress?.({ stage: 'upload', done: 0, total: uploads.length, message: `Uploading ${uploads.length} symbol image(s)…` });
  await Promise.allSettled(uploads);
  onProgress?.({ stage: 'done', done: result.matched, total: symbols.length });
  return result;
}

// Parse a "pages to read" spec ("3", "3-4, 40-42", "AUTO") into 1-based page numbers.
function parsePagesSpec(spec: string, max: number): number[] {
  const out = new Set<number>();
  for (const tok of (spec ?? '').split(',').map((t) => t.trim()).filter(Boolean)) {
    const m = tok.match(/^(\d+)\s*-\s*(\d+)$/);
    if (m) { const a = Math.min(+m[1]!, +m[2]!), b = Math.max(+m[1]!, +m[2]!); for (let i = a; i <= b; i++) if (i >= 1 && i <= max) out.add(i); }
    else if (/^\d+$/.test(tok)) { const n = +tok; if (n >= 1 && n <= max) out.add(n); }
  }
  return [...out].sort((a, b) => a - b);
}

/**
 * AUTOMATIC legend extraction — used by the Analyze flow so the estimator never
 * has to run a separate step. Finds which page is the legend (the page whose
 * text best matches the symbol labels), then crops every symbol from it.
 *
 * @param pageSpec the "pages to read" string the user entered for the plans
 *                 (limits the legend scan; blank/AUTO → scan the first pages).
 */
export async function autoCropLegend(
  file: File,
  projectId: string,
  symbols: SymbolForMatching[],
  pageSpec: string,
  onProgress?: (p: ExtractProgress) => void,
): Promise<ExtractResult & { legendPage: number | null }> {
  if (!symbols.length) return { matched: 0, unmatched_symbols: [], white_patches: [], errors: ['No symbols to crop.'], legendPage: null };
  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;

  // Candidate pages to scan: the user's page spec if numeric, else first N.
  let candidates = parsePagesSpec(pageSpec, doc.numPages);
  if (!candidates.length) {
    const cap = Math.min(doc.numPages, 25);   // legends are early; bound the scan
    candidates = Array.from({ length: cap }, (_, i) => i + 1);
  }

  // Build normalized target labels from the symbols.
  const targets = symbols
    .map((s) => normalize(s.legend_description || s.symbol_tag || ''))
    .filter((t) => t.length >= 3);

  // Score each candidate page by how many symbol labels its text contains.
  let bestPage = 0, bestScore = -1;
  for (let i = 0; i < candidates.length; i++) {
    const p = candidates[i]!;
    onProgress?.({ stage: 'find', done: i, total: candidates.length, message: `Looking for the legend (page ${p})…` });
    let pageNorms: string[];
    try {
      const page = await doc.getPage(p);
      const tc = await page.getTextContent();
      pageNorms = (tc.items as Array<{ str?: string }>).map((it) => normalize(it.str)).filter(Boolean);
    } catch { continue; }
    if (!pageNorms.length) continue;
    const pageSet = new Set(pageNorms);
    let score = 0;
    for (const t of targets) {
      if (pageSet.has(t)) { score++; continue; }
      for (const pn of pageNorms) { if (pn.length >= 4 && (pn.includes(t) || t.includes(pn))) { score++; break; } }
    }
    if (score > bestScore) { bestScore = score; bestPage = p; }
  }

  if (!bestPage || bestScore <= 0) {
    return {
      matched: 0,
      unmatched_symbols: symbols.map((s) => s.legend_description || s.symbol_tag || s.id),
      white_patches: [], errors: ['Could not find a legend page whose text matches the symbols (the plan may have no text layer).'],
      legendPage: null,
    };
  }

  // Crop every symbol from the detected legend page.
  const r = await extractLegendImages(file, bestPage, projectId, symbols, onProgress);
  return { ...r, legendPage: bestPage };
}

// TODO: OCR fallback for fully scanned PDFs (no text layer). Tesseract.js can
// produce text + bbox per word, which feeds the same matcher. ~5-10s/page on a
// modern CPU. Defer until we hit a real scanned-only file in the field.
